import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  IQuickAction,
  IQuickActionDomain,
  IQuickActionsResponse
} from '../../interfaces/quick-action.interface';
import { quickActionIcon } from '../../utils/quick-action-icon.util';

interface IDomainWithActions {
  domain: IQuickActionDomain;
  actions: IQuickAction[];
}

@Component({
  selector: 'sgt-quick-actions-menu',
  templateUrl: './quick-actions-menu.component.html',
  styleUrls: ['./quick-actions-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickActionsMenuComponent implements OnChanges, AfterViewInit {
  @Input() quickActions: IQuickActionsResponse;
  @Input() searchQuery = '';
  @Input() canGoBack = false;

  @Output() quickActionSelected = new EventEmitter<IQuickAction>();
  @Output() searchQueryChange = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  @ViewChild('listRef') listRef: ElementRef<HTMLDivElement>;
  @ViewChild('searchRef') searchRef: ElementRef<HTMLInputElement>;

  filteredActions: IQuickAction[] = [];
  highlightedIndex = -1;
  isSearching = false;
  domainsWithActions: IDomainWithActions[] = [];
  navigableActions: IQuickAction[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.quickActions || changes.searchQuery) {
      this.isSearching = this.searchQuery.trim().length > 0;
      this.highlightedIndex = -1;
      this.refresh();
    }
  }

  moveHighlight(direction: 1 | -1): void {
    const len = this.navigableActions.length;
    if (!len) {
 return;
}
    this.highlightedIndex = (this.highlightedIndex + direction + len) % len;
    this.cdr.markForCheck();
    this.scrollToHighlighted();
  }

  selectHighlighted(): boolean {
    if (this.highlightedIndex >= 0 && this.highlightedIndex < this.navigableActions.length) {
      this.onActionClick(this.navigableActions[this.highlightedIndex]);
      return true;
    }
    return false;
  }

  setHighlight(index: number): void {
    this.highlightedIndex = index;
  }

  ngAfterViewInit(): void {
    this.searchRef?.nativeElement.focus();
  }

  onSearch(event: Event): void {
    this.searchQueryChange.emit((event.target as HTMLInputElement).value);
  }

  /** As setas e o Enter pertencem à lista; o resto é digitação normal. */
  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Enter' && this.selectHighlighted()) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closed.emit();
    }
  }

  iconOf(action: IQuickAction): string {
    return quickActionIcon(action.icon);
  }

  onActionClick(action: IQuickAction): void {
    this.quickActionSelected.emit(action);
  }

  private refresh(): void {
    this.domainsWithActions = this.buildDomains();
    const allActions = this.domainsWithActions.flatMap(e => e.actions);
    this.filteredActions = this.isSearching ? this.filter(allActions) : [];
    this.navigableActions = this.isSearching ? this.filteredActions : allActions;
  }

  private buildDomains(): IDomainWithActions[] {
    if (!this.quickActions) {
 return [];
}
    return this.quickActions.domains
      .map(domain => ({ domain, actions: domain.actions }))
      .filter(entry => entry.actions.length > 0);
  }

  private filter(actions: IQuickAction[]): IQuickAction[] {
    const query = this.searchQuery.toLowerCase();
    return actions.filter(a => a.title.toLowerCase().includes(query));
  }

  private scrollToHighlighted(): void {
    setTimeout(() => {
      if (!this.listRef) {
 return;
}
      const active = this.listRef.nativeElement.querySelector(
        '.quick-actions-menu__item--active'
      ) as HTMLElement;
      if (active) {
        active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }
}
